import React, { useState, useEffect, type FormEvent, type ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from './button';
import { Spinner } from './spinner';
import { cn } from '@/lib/utils';

export interface FormField {
  name: string;
  label: string;
  type: 'text' | 'email' | 'number' | 'select' | 'textarea' | 'checkbox' | 'date';
  placeholder?: string;
  required?: boolean;
  options?: readonly { value: string; label: string }[];
  helperText?: string;
  disabled?: boolean;
}

interface ModalFormProps<T> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  fields: FormField[];
  initialData?: Partial<T>;
  onSubmit: (data: Partial<T>) => Promise<void> | void;
  submitLabel?: string;
  cancelLabel?: string;
  isLoading?: boolean;
  validationErrors?: Record<string, string>;
  className?: string;
  children?: ReactNode;
}

export function ModalForm<T extends Record<string, any>>({
  open,
  onOpenChange,
  title,
  description,
  fields,
  initialData = {},
  onSubmit,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  isLoading = false,
  validationErrors = {},
  className,
  children,
}: ModalFormProps<T>) {
  const [formData, setFormData] = useState<Partial<T>>(initialData);
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens/closes or initialData changes
  useEffect(() => {
    if (open) {
      setFormData(initialData);
      setLocalErrors({});
    }
  }, [open, initialData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear error when user starts typing
    if (localErrors[name]) {
      setLocalErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Basic validation
    const errors: Record<string, string> = {};
    fields.forEach((field) => {
      if (field.required && !formData[field.name]) {
        errors[field.name] = `${field.label} is required`;
      }
      if (field.type === 'email' && formData[field.name]) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(String(formData[field.name]))) {
          errors[field.name] = 'Invalid email format';
        }
      }
      if (field.type === 'number' && formData[field.name] !== undefined && formData[field.name] !== '') {
        const num = Number(formData[field.name]);
        if (isNaN(num) || num < 0) {
          errors[field.name] = 'Must be a positive number';
        }
      }
    });

    if (Object.keys(errors).length > 0) {
      setLocalErrors(errors);
      return;
    }

    await onSubmit(formData);
  };

  const getFieldError = (fieldName: string) => {
    return validationErrors[fieldName] || localErrors[fieldName];
  };

  const renderField = (field: FormField) => {
    const error = getFieldError(field.name);
    const value = formData[field.name];

    switch (field.type) {
      case 'select':
        return (
          <div key={field.name} className="space-y-2">
            <label htmlFor={field.name} className="text-sm font-semibold text-primary-800">
              {field.label}
              {field.required && <span className="text-red-600 ml-1">*</span>}
            </label>
            <select
              id={field.name}
              name={field.name}
              value={value || ''}
              onChange={handleChange}
              disabled={field.disabled || isLoading}
              className={cn(
                'w-full h-10 px-3 rounded-lg border border-border bg-white text-text-primary text-sm',
                'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
                error && 'border-red-500 focus:ring-red-500',
                field.disabled && 'bg-background-subtle text-text-muted cursor-not-allowed'
              )}
              required={field.required}
            >
              <option value="">Select {field.label}</option>
              {field.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {field.helperText && (
              <p className="text-xs text-text-muted">{field.helperText}</p>
            )}
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
        );

      case 'textarea':
        return (
          <div key={field.name} className="space-y-2">
            <label htmlFor={field.name} className="text-sm font-semibold text-primary-800">
              {field.label}
              {field.required && <span className="text-red-600 ml-1">*</span>}
            </label>
            <textarea
              id={field.name}
              name={field.name}
              value={value || ''}
              onChange={handleChange}
              placeholder={field.placeholder}
              disabled={field.disabled || isLoading}
              rows={4}
              className={cn(
                'w-full px-3 py-2 rounded-lg border border-border bg-white text-text-primary text-sm',
                'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
                error && 'border-red-500 focus:ring-red-500',
                field.disabled && 'bg-background-subtle text-text-muted cursor-not-allowed'
              )}
              required={field.required}
            />
            {field.helperText && (
              <p className="text-xs text-text-muted">{field.helperText}</p>
            )}
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
        );

      case 'checkbox':
        return (
          <div key={field.name} className="flex items-center space-x-2">
            <input
              type="checkbox"
              id={field.name}
              name={field.name}
              checked={!!value}
              onChange={handleChange}
              disabled={field.disabled || isLoading}
              className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
            />
            <label htmlFor={field.name} className="text-sm font-medium text-text-primary">
              {field.label}
            </label>
            {field.helperText && (
              <p className="text-xs text-text-muted">{field.helperText}</p>
            )}
          </div>
        );

      default:
        return (
          <div key={field.name} className="space-y-2">
            <label htmlFor={field.name} className="text-sm font-semibold text-primary-800">
              {field.label}
              {field.required && <span className="text-red-600 ml-1">*</span>}
            </label>
            <input
              id={field.name}
              name={field.name}
              type={field.type}
              value={value || ''}
              onChange={handleChange}
              placeholder={field.placeholder}
              disabled={field.disabled || isLoading}
              className={cn(
                'w-full h-10 px-3 rounded-lg border border-border bg-white text-text-primary text-sm',
                'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
                error && 'border-red-500 focus:ring-red-500',
                field.disabled && 'bg-background-subtle text-text-muted cursor-not-allowed'
              )}
              required={field.required}
            />
            {field.helperText && (
              <p className="text-xs text-text-muted">{field.helperText}</p>
            )}
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-h-[90vh] overflow-y-auto', className)}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-2">
          {children}
          {fields.map(renderField)}
        </form>
        <DialogFooter className="gap-2 sm:gap-0 pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            {cancelLabel}
          </Button>
          <Button type="submit" onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? (
              <>
                <Spinner className="mr-2" size="sm" />
                Saving...
              </>
            ) : (
              submitLabel
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
